import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { Button, Flex, Icon, M } from 'design-system-web';
import {
  HookFormDropdown,
  HookFormMultiSelectDropdown,
  HookFormTimePicker,
} from 'components/reactHookForm';
import { Assignable } from 'domains-work-order-type-contracts';
import { buildEndTimes, buildStartTimes } from './coverageHoursTimeUtils';
import { DAY_OF_WEEK_ITEMS, TeamCoverageEntry } from './coverageHoursTeamUtils';
import { useCoverageHoursOverlap } from './useCoverageHoursOverlap';
import styles from './CreateCoverageHoursPanel.scss';

type CoverageBlockState = {
  daysOfWeek: string[];
  startTime: string | null;
  endTime: string | null;
};

type AddTeamFormValues = {
  teamId: string | null;
  blocks: CoverageBlockState[];
};

type AddTeamFormProps = {
  assignableTeams: Assignable[];
  initialEntry?: TeamCoverageEntry;
  onDone: (entry: TeamCoverageEntry) => void;
  onDiscard: () => void;
  savedTeams: TeamCoverageEntry[];
  editingEntryId?: string;
  channelId: string;
  category: string;
  issue: string;
};

const EMPTY_BLOCK: CoverageBlockState = {
  daysOfWeek: [],
  startTime: null,
  endTime: null,
};

export function AddTeamForm({
  assignableTeams,
  initialEntry,
  onDone,
  onDiscard,
  savedTeams,
  editingEntryId,
  channelId,
  category,
  issue,
}: AddTeamFormProps) {
  const { t } = useTranslation();

  const {
    control,
    watch,
    handleSubmit,
    formState: { isValid },
  } = useForm<AddTeamFormValues>({
    mode: 'onChange',
    defaultValues: {
      teamId: initialEntry?.teamId ?? null,
      blocks: initialEntry?.coverageBlocks.length
        ? initialEntry.coverageBlocks.map(b => ({
            daysOfWeek: b.daysOfWeek,
            startTime: b.startTime,
            endTime: b.endTime,
          }))
        : [EMPTY_BLOCK],
    },
  });

  const { hasConflictAt, validateNoOverlap } = useCoverageHoursOverlap({
    control,
    bankedEntries: savedTeams,
    editingEntryId,
    channelId,
    category,
    issue,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'blocks',
    rules: { validate: validateNoOverlap },
  });
  const watchedBlocks = watch('blocks');

  const teamItems = useMemo(
    () => assignableTeams.map(team => ({ label: team.label, value: team.value })),
    [assignableTeams]
  );

  const onSubmit = (values: AddTeamFormValues) => {
    const teamLabel = teamItems.find(item => item.value === values.teamId)?.label ?? '';

    onDone({
      id: initialEntry?.id ?? uuidv4(),
      teamId: values.teamId as string,
      teamLabel,
      coverageBlocks: values.blocks.map(b => ({
        daysOfWeek: b.daysOfWeek,
        startTime: b.startTime as string,
        endTime: b.endTime as string,
      })),
    });
  };

  const addBlockLabel =
    fields.length === 1
      ? t('web.admin.serviceRequest.settings.coverageHours.create.addCoverageBlock')
      : t('web.admin.serviceRequest.settings.coverageHours.create.addHours');

  return (
    <Flex direction="column" gap={4} className={styles.addTeamForm}>
      <div className={styles.coverageBlockGrid}>
        <HookFormDropdown
          control={control}
          name="teamId"
          id="coverage-hours-team-select"
          testId="coverage-hours-team-dropdown"
          doTranslation={false}
          isFullWidth
          items={teamItems}
          returnValueOnly
          label={t('web.admin.serviceRequest.settings.coverageHours.create.team')}
          placeholder={t(
            'web.admin.serviceRequest.settings.coverageHours.create.teamPlaceholder'
          )}
          fixedLabel
          isRequired
        />
      </div>

      {fields.map((block, index) => (
        <Flex key={block.id} direction="column" gap={1}>
          <Flex align="flex-end" gap={2}>
            <div
              style={{ flex: 1, minWidth: 0 }}
              className={styles.coverageBlockGrid}
            >
              <HookFormMultiSelectDropdown
                control={control}
                name={`blocks.${index}.daysOfWeek`}
                testId={
                  index === 0
                    ? 'coverage-hours-days-dropdown'
                    : `coverage-hours-days-dropdown-${index}`
                }
                doTranslation={false}
                items={DAY_OF_WEEK_ITEMS}
                returnValueOnly
                isRequired
                label={t(
                  'web.admin.serviceRequest.settings.coverageHours.create.dayOfWeek'
                )}
                placeholder={t(
                  'web.admin.serviceRequest.settings.coverageHours.create.dayOfWeekPlaceholder'
                )}
                fixedLabel
                truncateSelectedItems
              />
              <HookFormTimePicker
                control={control}
                name={`blocks.${index}.startTime`}
                testId={
                  index === 0
                    ? 'coverage-hours-start-time'
                    : `coverage-hours-start-time-${index}`
                }
                label={t(
                  'web.admin.serviceRequest.settings.coverageHours.create.startTime'
                )}
                times={buildStartTimes(watchedBlocks[index]?.endTime ?? null)}
                showTimezone={false}
                showClear={false}
                fixedLabel
                isRequired
              />
              <HookFormTimePicker
                control={control}
                name={`blocks.${index}.endTime`}
                testId={
                  index === 0
                    ? 'coverage-hours-end-time'
                    : `coverage-hours-end-time-${index}`
                }
                label={t(
                  'web.admin.serviceRequest.settings.coverageHours.create.endTime'
                )}
                times={buildEndTimes(watchedBlocks[index]?.startTime ?? null)}
                showTimezone={false}
                showClear={false}
                fixedLabel
                isRequired
              />
            </div>
            {fields.length > 1 &&
              (index === 0 ? (
                <div className={styles.trashButtonSpacer} />
              ) : (
                <Button
                  variant="text"
                  testId={`coverage-hours-remove-block-${index}`}
                  onClick={() => remove(index)}
                >
                  <Icon name="trash-alt" set="FontAwesome" type="far" />
                </Button>
              ))}
          </Flex>

          {hasConflictAt(index) && (
            <M
              className={styles.overlapError}
              data-test={`coverage-hours-overlap-error-${index}`}
            >
              {t(
                'web.admin.serviceRequest.settings.coverageHours.create.overlapError'
              )}
            </M>
          )}
        </Flex>
      ))}

      <Flex>
        <Button
          variant="text"
          size="large"
          testId="coverage-hours-add-coverage-block"
          onClick={() => append(EMPTY_BLOCK)}
        >
          {addBlockLabel}
        </Button>
      </Flex>

      <Flex gap={3}>
        <Button
          variant="primary"
          size="large"
          testId="coverage-hours-team-done"
          disabled={!isValid}
          onClick={handleSubmit(onSubmit)}
        >
          {t('web.admin.serviceRequest.settings.coverageHours.create.done')}
        </Button>
        <Button
          variant="secondary"
          size="large"
          testId="coverage-hours-team-discard"
          onClick={onDiscard}
        >
          {t(
            'web.admin.serviceRequest.settings.coverageHours.create.discard'
          )}
        </Button>
      </Flex>
    </Flex>
  );
}
